"use client";

import React, {Fragment} from "react";
import {useSearchParams} from "next/navigation";
import {Locale} from "~/i18n-config";
import {LangLink} from "~/components/locales/LangLink";
import {Menu,Transition} from "@headlessui/react";
import {FaArrowCircleRight} from "react-icons/fa";
import {RiAccountCircleFill} from "react-icons/ri";
import {IoMdBusiness} from "react-icons/io";
import {MdAccountCircle, MdLocalHospital, MdOutlineManageAccounts, MdLogout} from "react-icons/md";
import clsx from "clsx";
import {InlineText} from "@/components/content/common/InlineText";

type AccountNavigationDictionary = {
    account: string;
    navAccountAdmin: string;
    navAccountBusiness: string;
    navAccountHome: string;
    navAccountLogout: string;
    navAccountSettings: string;
};

type AccountNavigationProps = {
    lang: Locale;
    dictionary: AccountNavigationDictionary;
    firstname?: string;
    lastname?: string;
    hasPermAdmin: boolean;
    hasPermAgent: boolean;
    hasPermBusiness: boolean;
    layout?: string | null;
};

export function AccountNavigation(
    { lang, dictionary, firstname, lastname, hasPermAdmin, hasPermAgent, hasPermBusiness, layout } : AccountNavigationProps
) {

    const searchParams = useSearchParams();
    const accountName = [firstname, lastname].filter(Boolean).join(" ").trim();

    return (
        <Menu as="div" className="relative min-w-0 font-jost">
            {({ open, close }) => (
                <>
                    <Menu.Button
                        aria-label={accountName || dictionary.account}
                        className={clsx(
                            open ? "outline-white/20" : "outline-white hover:outline-white/50",
                            "inline-flex h-10 max-w-full flex-row items-center justify-center gap-2 box-border rounded-md py-2 px-4 text-normal font-jost font-semibold border-0 outline outline-offset-2 text-white transition-all duration-300",
                        )}>
                        {accountName ? (
                            <span className="min-w-0 max-w-[50vw] truncate text-left capitalize sm:max-w-[220px]">{accountName}</span>
                        ) : (
                            <MdAccountCircle aria-hidden="true" size="20px" color="#ffffff" />
                        )}
                        {accountName && (
                            <FaArrowCircleRight aria-hidden="true" size={"20px"} color="#ffffff" className={clsx(open ? "rotate-90" : "rotate-0", "ml-1 shrink-0 transition-rotate duration-300")} />
                        )}
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95">
                        <Menu.Items className="absolute right-0 z-999 mt-4 w-48 max-w-[calc(100vw-1rem)] origin-top-right overflow-hidden rounded-md shadow-lg outline outline-offset-0 outline-white">
                            <Menu.Item>
                                <LangLink
                                    lang={lang}
                                    href="/dashboard"
                                    onClick={close}
                                    className="group inline-flex items-center justify-start gap-2 w-full font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150 rounded-t-md">
                                    <span className="flex group-hover:hidden shrink-0"><MdLocalHospital size="20px" color="#ffffff"/></span>
                                    <span className="hidden group-hover:flex shrink-0"><MdLocalHospital size="20px" color="#27272a"/></span>
                                    <span className="min-w-0">{dictionary.navAccountHome}</span>
                                </LangLink>
                            </Menu.Item>
                            {hasPermBusiness && layout !== "business" && (
                                <Menu.Item>
                                    <LangLink
                                        lang={lang}
                                        href={{ pathname: "/business", query: Object.fromEntries(searchParams.entries()) }}
                                        onClick={close}
                                        className="group inline-flex items-center justify-start gap-2 w-full font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150">
                                        <span className="flex group-hover:hidden shrink-0"><IoMdBusiness size="20px" color="#ffffff"/></span>
                                        <span className="hidden group-hover:flex shrink-0"><IoMdBusiness size="20px" color="#27272a"/></span>
                                        <span className="min-w-0 leading-tight"><InlineText value={dictionary.navAccountBusiness} /></span>
                                    </LangLink>
                                </Menu.Item>
                            )}
                            {(hasPermAdmin || hasPermAgent) && layout !== "management" && (
                                <Menu.Item>
                                    <LangLink
                                        lang={lang}
                                        href={"/management"}
                                        onClick={close}
                                        className="group inline-flex items-center justify-start gap-2 w-full font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150">
                                        <span className="flex group-hover:hidden shrink-0"><MdOutlineManageAccounts size="20px" color="#ffffff"/></span>
                                        <span className="hidden group-hover:flex shrink-0"><MdOutlineManageAccounts size="20px" color="#27272a"/></span>
                                        <span className="min-w-0">{dictionary.navAccountAdmin}</span>
                                    </LangLink>
                                </Menu.Item>
                            )}
                            <Menu.Item>
                                <LangLink
                                    lang={lang}
                                    href="/account"
                                    onClick={close}
                                    className="group inline-flex items-center justify-start gap-2 w-full font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150">
                                    <span className="flex group-hover:hidden shrink-0"><RiAccountCircleFill size="20px" color="#ffffff"/></span>
                                    <span className="hidden group-hover:flex shrink-0"><RiAccountCircleFill size="20px" color="#27272a"/></span>
                                    <span className="min-w-0">{dictionary.navAccountSettings}</span>
                                </LangLink>
                            </Menu.Item>
                            <Menu.Item>
                                <form action="/auth/logout" method="post">
                                    <input type="hidden" name="lang" value={lang} />
                                    <button className="group inline-flex items-center justify-start gap-2 w-full text-left font-semibold px-4 py-3 text-white bg-grass-500/90 hover:text-zinc-800 hover:bg-white transition-all duration-150 rounded-b-md">
                                        <span className="flex group-hover:hidden shrink-0"><MdLogout size="20px" color="#ffffff" /></span>
                                        <span className="hidden group-hover:flex shrink-0"><MdLogout size="20px" color="#27272a"/></span>
                                        <span className="min-w-0">{dictionary.navAccountLogout}</span>
                                    </button>
                                </form>
                            </Menu.Item>
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    );

}
